"use client";

import { m } from "motion/react";
import Image from "next/image";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import BlueprintBackground from "@/components/BlueprintBackground";
import MetadataUpdater from "@/components/MetadataUpdater";

type Partner = {
  name: string;
  logo: string;
  description: {
    de: string;
    en: string;
  };
  url: string;
};

export const clusters: Partner[] = [
  {
    name: "AT Styria",
    logo: "/cluster-logos/Logo_AT_STYRIA_new.webp",
    description: {
      de: "Die Plattform für Automatisierungstechnik in der Steiermark. Vernetzung von Unternehmen, Forschung und Bildung zur Stärkung der regionalen Wirtschaft.",
      en: "The platform for automation technology in Styria. Connecting companies, research, and education to strengthen the regional economy.",
    },
    url: "https://at-styria.at",
  },
  {
    name: "Green Tech Valley",
    logo: "/cluster-logos/green-tech-valley.webp",
    description: {
      de: "Europas Spitzenregion für Energie- und Umwelttechnik. Über 300 Unternehmen und Forschungseinrichtungen in der Steiermark und Kärnten.",
      en: "Europe's top region for energy and environmental technology. Over 300 companies and research institutions in Styria and Carinthia.",
    },
    url: "https://greentech.at",
  },
  {
    name: "Silicon Alps Cluster",
    logo: "/cluster-logos/silicon-alps-cluster.webp",
    description: {
      de: "Der führende EBS (Electronic Based Systems) Cluster Österreichs. Verbindet Mikroelektronik, Sensorik und Systemtechnologie-Unternehmen.",
      en: "Austria's leading EBS (Electronic Based Systems) cluster. Connecting microelectronics, sensor technology, and systems technology companies.",
    },
    url: "https://silicon-alps.at",
  },

];

// Partner companies — scaffold for future use (hidden until populated)
export const partners: Partner[] = [
  {
    name: "E.S.Elektronik GmbH",
    logo: "/partner-logos/logo-ese.webp",
    description: {
      de: "Praxisorientierter Elektronik-Partner für robuste Industrie-Lösungen: Entwicklung, Fertigung und technische Umsetzung – ideal für Systeme, die im Feld zuverlässig laufen müssen.",
      en: "Practical electronics partner for robust industrial solutions: development, manufacturing, and technical implementation – ideal for systems that need to run reliably in the field.",
    },
    url: "https://www.ese.at",
  },
];

export default function PartnersPage() {
  const { t, locale } = useTranslation();

  return (
    <>
      <MetadataUpdater
        titleKey="meta.partners.title"
        descriptionKey="meta.partners.description"
      />
      <BlueprintBackground />
      <main className="min-h-screen pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16"
          >
            <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-4">
              {t("partners.page.label")}
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-8xl font-heading font-black text-text-primary leading-[0.9] tracking-tighter uppercase mb-6">
              {t("partners.page.title")}
            </h1>
            <p className="text-lg sm:text-xl text-text-muted max-w-2xl">
              {t("partners.page.description")}
            </p>
          </m.div>

          {/* Partner Companies — hidden scaffold for future use */}
          <div className="mb-12">
            <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-6">
              {locale === "de" ? "Partnerunternehmen" : "Partner Companies"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {partners.map((partner, i) => (
                <m.div
                  key={partner.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="border border-border-faint bg-bg-card p-6 hover:border-mint-text/20 transition-all group"
                >
                  <div className="h-20 flex items-center justify-center mb-4 border-b border-border-faint pb-4">
                    {partner.logo ? (
                      <Image
                        src={partner.logo}
                        alt={partner.name}
                        width={160}
                        height={64}
                        className="max-h-14 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all"
                      />
                    ) : (
                      <div className="text-sm font-mono text-text-faintest group-hover:text-text-muted uppercase tracking-widest transition-colors">
                        {partner.name}
                      </div>
                    )}
                  </div>
                  <p className="text-text-muted text-sm leading-relaxed mb-4">
                    {locale === "de" ? partner.description.de : partner.description.en}
                  </p>
                  {partner.url && (
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-mint-text text-xs font-mono uppercase tracking-widest hover:underline"
                    >
                      Website <ExternalLink size={12} />
                    </a>
                  )}
                </m.div>
              ))}
            </div>
          </div>

          {/* Cluster Partners */}
          <div className="mt-16">
            <div className="text-mint-text font-mono text-[10px] tracking-[0.3em] uppercase mb-6">
              {locale === "de" ? "Cluster-Mitgliedschaften" : "Cluster Memberships"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {clusters.map((cluster, i) => (
                <m.div
                  key={cluster.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="border border-border-faint bg-bg-card p-6 hover:border-mint-text/20 transition-all group"
                >
                  <div className="h-20 flex items-center justify-center mb-4 border-b border-border-faint pb-4">
                    <Image
                      src={cluster.logo}
                      alt={cluster.name}
                      width={160}
                      height={64}
                      className="max-h-14 w-auto object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all"
                    />
                  </div>
                  <h3 className="text-sm font-bold text-text-primary mb-2">
                    {cluster.name}
                  </h3>
                  <p className="text-text-muted text-sm leading-relaxed mb-4">
                    {locale === "de" ? cluster.description.de : cluster.description.en}
                  </p>
                  {cluster.url && (
                    <a
                      href={cluster.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-mint-text text-xs font-mono uppercase tracking-widest hover:underline"
                    >
                      Website <ExternalLink size={12} />
                    </a>
                  )}
                </m.div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
